import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Grid,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { Download } from "lucide-react";
import { IconTableExport } from "@tabler/icons-react";
import moment from "moment";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAutocomplete from "components/MDAutocomplete";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import handleExport from "utils/exportData";

const SubmissionResults = () => {
  const { formId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [formDetails, setFormDetails] = useState(null);
  const [filters, setFilters] = useState({
    user: null,
    district_name: null,
  });

  // Fetch districts for filter
  const fetchDistricts = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/districts/all`, {
      withCredentials: true,
    });
    return res.data.data;
  };

  const { data: districts } = useQuery({
    queryKey: ["districts_res"],
    queryFn: fetchDistricts,
  });

  // Fetch users for filter
  const fetchUsersList = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/all?page=1&limit=10000`, {
      withCredentials: true,
    });
    return res.data.data;
  };

  const { data: usersData } = useQuery({
    queryKey: ["usersList_res"],
    queryFn: fetchUsersList,
  });

  const allUsersOptions = useMemo(() => {
    if (!usersData) return [];
    return usersData.map((user) => ({
      label: user.user_name,
      value: user.user_id,
    }));
  }, [usersData]);

  const districtOptions = useMemo(() => {
    if (!districts) return [];
    return districts.map((d) => ({
      label: d.title,
      value: d.value,
    }));
  }, [districts]);

  const fetchData = async (
    pageIndex = pagination.pageIndex,
    pageSize = pagination.pageSize,
    filterValues = filters,
    sortValues = sorting
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
        sorting: JSON.stringify(sortValues),
        user: filterValues.user || "",
        district: filterValues.district_name || "", // Changed from district_name to district to match typical backend naming
      });

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/forms/${formId}?${queryParams.toString()}`,
        { withCredentials: true }
      );

      if (res.data.status === 200) {
        setFormDetails(res.data.data);
        setData(res.data.data.submissions || []);
        setRowCount(res.data.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching submissions:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [formId, pagination.pageIndex, pagination.pageSize, sorting]);

  const columns = useMemo(() => {
    if (!formDetails || !formDetails.fields) return [];

    const baseColumns = [
      {
        accessorKey: "user_name",
        header: "User Name",
        size: 150,
        muiTableBodyCellProps: { align: "center" },
        muiTableHeadCellProps: { align: "center" },
      },
      {
        accessorKey: "district_name",
        header: "District Name",
        size: 150,
        muiTableBodyCellProps: { align: "center" },
        muiTableHeadCellProps: { align: "center" },
      },
      {
        accessorKey: "created_at",
        header: "Submitted At",
        size: 150,
        muiTableBodyCellProps: { align: "center" },
        muiTableHeadCellProps: { align: "center" },
        Cell: ({ cell }) => moment(cell.getValue()).format("DD-MM-YYYY"),
      },
    ];

    const dynamicColumns = formDetails.fields.map((field) => ({
      accessorKey: field.id,
      header: field.label,
      size: 150,
      muiTableBodyCellProps: { align: "center" },
      muiTableHeadCellProps: { align: "center" },
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (Array.isArray(value)) return value.join(", ");
        if (typeof value === "object" && value !== null) return JSON.stringify(value);
        return value || "-";
      },
    }));

    return [...baseColumns, ...dynamicColumns];
  }, [formDetails]);

  const handleExportData = () => {
    if (!formDetails) return;
    handleExport(formDetails, "Form_Submissions_List_Data");
  };

  const handleSearch = () => {
    setPagination({ ...pagination, pageIndex: 0 });
    fetchData(0, pagination.pageSize, filters, sorting);
  };

  const handleCancel = () => {
    const cleared = { user: null, district_name: null };
    setFilters(cleared);
    setPagination({ ...pagination, pageIndex: 0 });
    fetchData(0, pagination.pageSize, cleared, sorting);
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} />
      <Card id="submission-filters-card" sx={{ mb: 2 }}>
        <MDBox pt={2} px={2} pb={3}>
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid item xs={12} sm={6}>
              <MDTypography variant="h5" fontWeight="medium">
                {formDetails?.title || "Submission Results"}
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="User Name"
                value={filters.user}
                options={allUsersOptions}
                onChange={(newValue) => setFilters({ ...filters, user: newValue })}
                isFilter={true}
                getOptionLabel={(option) => option.label}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="District"
                value={filters.district_name}
                options={districtOptions}
                onChange={(newValue) => setFilters({ ...filters, district_name: newValue })}
                isFilter={true}
                getOptionLabel={(option) => option.label}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={handleSearch}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": { backgroundColor: "#155fa0" },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Box>
        <Grid container spacing={5}>
          <Grid item size={{ xs: 12, md: 12 }} className="listTable">
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Paper
                elevation={2}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  p: 2,
                }}
              >
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  getRowId={(row) => row.submission_id}
                  enableFilters={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  layoutMode="grid"
                  enableRowNumbers
                  enableStickyHeader
                  onPaginationChange={setPagination}
                  onSortingChange={setSorting}
                  rowCount={rowCount}
                  state={{
                    pagination,
                    sorting,
                    isLoading: loading,
                  }}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#004030",
                      color: "#fff",
                      fontWeight: "bold",
                      textAlign: "center",
                      justifyContent: "center",
                      "& .MuiTableCell-content": {
                        justifyContent: "center",
                      },
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      "& .MuiTableSortLabel-root": {
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        fontSize: "12px",
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      padding: "8px",
                      textAlign: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    sx: {
                      backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0",
                    },
                  })}
                  renderTopToolbarCustomActions={() => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        flexWrap: "wrap",
                        marginLeft: "auto",
                      }}
                    >
                      <Tooltip title={"Export data to excel sheet"} arrow>
                        <MDButton
                          onClick={handleExportData}
                          sx={{
                            background:
                              "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                            color: "#f4f4f4",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            transition: "background 0.3s, transform 0.3s",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%)",
                              transform: "scale(1.05)",
                            },
                          }}
                          startIcon={<IconTableExport />}
                        >
                          {"Export To Excel"}
                        </MDButton>
                      </Tooltip>
                    </Box>
                  )}
                  muiTopToolbarProps={{
                    sx: {
                      backgroundColor: "#fff",
                      boxShadow: "none",
                    },
                  }}
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Footer />
    </DashboardLayout>
  );
};

export default SubmissionResults;
